/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { billingDetailsService } from '../../services/api';
import type { BillingDetails, KYC } from '../../types';
import { Button, Skeleton } from '../console/ui';

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PIN_RE = /^[1-9][0-9]{5}$/;

export const EMPTY_BILLING_DETAILS: BillingDetails = {
  legal_name: '',
  gstin: '',
  billing_email: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: 'India',
};

/** Billing details derived from KYC, used to prefill fields the backend left empty. */
export const kycBillingDefaults = (kyc?: KYC | null): Partial<BillingDetails> =>
  kyc
    ? {
        legal_name: kyc.gst_verified_legal_name || kyc.organisation_name || kyc.business_name || kyc.full_name || '',
        gstin: kyc.gst_number && GSTIN_RE.test(kyc.gst_number.toUpperCase()) ? kyc.gst_number.toUpperCase() : '',
        address_line1: kyc.address_line1 || '',
        address_line2: kyc.address_line2 || '',
        city: kyc.city || '',
        state: kyc.state || '',
        postal_code: kyc.pin_code || '',
      }
    : {};

/** Fill empty fields of `base` from `fallback`. */
export const mergeBillingDefaults = (base: BillingDetails, fallback: Partial<BillingDetails>): BillingDetails => {
  const out = { ...base };
  (Object.keys(fallback) as Array<keyof BillingDetails>).forEach((k) => {
    if (!out[k] && fallback[k]) out[k] = fallback[k] as string;
  });
  return out;
};

const validate = (d: BillingDetails): Partial<Record<keyof BillingDetails, string>> => {
  const errors: Partial<Record<keyof BillingDetails, string>> = {};
  if (!d.legal_name.trim()) errors.legal_name = 'Enter the name to print on invoices.';
  if (d.gstin.trim() && !GSTIN_RE.test(d.gstin.trim().toUpperCase()))
    errors.gstin = 'GSTIN is 15 characters, like 22AAAAA0000A1Z5.';
  if (d.billing_email.trim() && !EMAIL_RE.test(d.billing_email.trim())) errors.billing_email = 'Enter a valid email.';
  if ((d.country || 'India').trim().toLowerCase() === 'india' && d.postal_code.trim() && !PIN_RE.test(d.postal_code.trim()))
    errors.postal_code = 'PIN code is 6 digits.';
  return errors;
};

const Field: React.FC<{
  id: keyof BillingDetails;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  placeholder?: string;
  type?: string;
  autoComplete?: string;
}> = ({ id, label, value, onChange, error, placeholder, type = 'text', autoComplete }) => (
  <label className="flex flex-col gap-1 text-[13px] text-ps-muted min-w-0" htmlFor={`bd-${id}`}>
    <span>{label}</span>
    <input
      id={`bd-${id}`}
      type={type}
      className="ps-input"
      value={value}
      placeholder={placeholder}
      autoComplete={autoComplete}
      aria-invalid={!!error}
      aria-describedby={error ? `bd-${id}-err` : undefined}
      onChange={(e) => onChange(e.target.value)}
    />
    {error && (
      <span id={`bd-${id}-err`} className="text-ps-err text-xs">
        {error}
      </span>
    )}
  </label>
);

/**
 * Editable "Billed to" identity for invoices (GET/PUT /billing-details).
 * Prefills from KYC until the user saves their own details.
 */
const BillingDetailsCard: React.FC<{
  details: BillingDetails;
  source: 'saved' | 'kyc' | 'empty';
  kyc?: KYC | null;
  loading?: boolean;
}> = ({ details, source, kyc, loading }) => {
  const qc = useQueryClient();
  const initial = useMemo(
    () => (source === 'saved' ? details : mergeBillingDefaults(details, kycBillingDefaults(kyc))),
    [details, source, kyc],
  );
  const [form, setForm] = useState<BillingDetails>(initial);
  const [dirty, setDirty] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  // Adopt fresh server/KYC values until the user starts editing.
  useEffect(() => {
    if (!dirty) setForm(initial);
  }, [initial, dirty]);

  const errors = validate(form);
  const hasErrors = Object.keys(errors).length > 0;
  const set = (k: keyof BillingDetails) => (v: string) => {
    setForm((f) => ({ ...f, [k]: k === 'gstin' ? v.toUpperCase() : v }));
    setDirty(true);
  };

  const save = useMutation({
    mutationFn: (d: BillingDetails) => billingDetailsService.update(d).then((r) => r.data),
    onSuccess: (resp) => {
      qc.setQueryData(['billingDetails'], resp);
      setDirty(false);
      setShowErrors(false);
      toast.success('Billing details saved');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Couldn’t save billing details'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    if (hasErrors) return;
    save.mutate({ ...form, gstin: form.gstin.trim().toUpperCase() });
  };
  const err = (k: keyof BillingDetails) => (showErrors ? errors[k] : undefined);

  return (
    <section className="ps-card p-5 flex flex-col gap-3 flex-[1_1_320px] min-w-0" aria-labelledby="gst">
      <h2 id="gst" className="m-0 text-base font-medium text-ps-text">
        Billing details on invoices
      </h2>
      {loading ? (
        <Skeleton className="h-64" />
      ) : (
        <form className="flex flex-col gap-3" onSubmit={onSubmit} noValidate>
          <Field
            id="legal_name"
            label="Legal name"
            value={form.legal_name}
            onChange={set('legal_name')}
            error={err('legal_name')}
            placeholder="Company or your name"
            autoComplete="organization"
          />
          <Field
            id="gstin"
            label="GSTIN (optional)"
            value={form.gstin}
            onChange={set('gstin')}
            error={err('gstin')}
            placeholder="22AAAAA0000A1Z5"
          />
          <Field
            id="billing_email"
            label="Billing email (optional)"
            type="email"
            value={form.billing_email}
            onChange={set('billing_email')}
            error={err('billing_email')}
            placeholder="accounts@company.com"
            autoComplete="email"
          />
          <Field
            id="address_line1"
            label="Billing address"
            value={form.address_line1}
            onChange={set('address_line1')}
            placeholder="Street"
            autoComplete="address-line1"
          />
          <Field
            id="address_line2"
            label="Address line 2 (optional)"
            value={form.address_line2}
            onChange={set('address_line2')}
            autoComplete="address-line2"
          />
          <div className="grid grid-cols-2 gap-3">
            <Field id="city" label="City" value={form.city} onChange={set('city')} autoComplete="address-level2" />
            <Field id="state" label="State" value={form.state} onChange={set('state')} autoComplete="address-level1" />
            <Field
              id="postal_code"
              label="PIN code"
              value={form.postal_code}
              onChange={set('postal_code')}
              error={err('postal_code')}
              autoComplete="postal-code"
            />
            <Field id="country" label="Country" value={form.country} onChange={set('country')} autoComplete="country-name" />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Button type="submit" disabled={save.isPending || (!dirty && source === 'saved')}>
              {save.isPending ? 'Saving…' : 'Save details'}
            </Button>
            {source !== 'saved' && !dirty && (
              <span className="text-xs text-ps-subtle">
                {source === 'kyc' || kyc ? 'Prefilled from your KYC. Save to use on invoices.' : 'Not saved yet.'}
              </span>
            )}
          </div>
        </form>
      )}
    </section>
  );
};

export default BillingDetailsCard;
